/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import OptionWrapper from "../others/option-wrapper";
import PropTypes from 'prop-types';
import { forwardRef, memo, useContext, useImperativeHandle, useMemo, useRef } from 'react';
import { boxBorder } from "../../styles/general";
import { useTheme } from "../../theme";
import { breakpointKeys } from "../../theme/createBreakpoints";
import { classNames, handleBreakpoints } from "../../utils";
import GridContext from "./context";
const {
  breakpoints: themeBreakpoints,
  spacing: themeSpacing
} = useTheme();
const Grid = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  children,
  className,
  columns: columnsProp,
  columnSpacing: columnSpacingProp,
  container,
  direction,
  id,
  item,
  leftSpacing: leftSpacingProp,
  lg,
  md,
  rowSpacing: rowSpacingProp,
  sm,
  spacing,
  style,
  topSpacing: topSpacingProp,
  verticalAlign,
  wrap,
  xl,
  xs,
  zeroMinWidth
}, reference) => {
  const columnsContext = useContext(GridContext);
  const rowSpacing = rowSpacingProp || spacing;
  const columnSpacing = columnSpacingProp || spacing;
  const columns = container ? columnsProp : columnsContext;
  const topSpacing = topSpacingProp !== null && topSpacingProp !== void 0 ? topSpacingProp : rowSpacing;
  const leftSpacing = leftSpacingProp !== null && leftSpacingProp !== void 0 ? leftSpacingProp : columnSpacing;
  const ref = useRef(null);
  const _GridCSS = GridCSS(direction, zeroMinWidth, container, item, wrap, verticalAlign);
  const _GridSpacingCSS = GridSpacingCSS(rowSpacing, columnSpacing, topSpacing, leftSpacing);
  const _HandleGridCSS = HandleGridCSS([xs, sm, md, lg, xl], columns);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  return useMemo(() => {
    return jsx("div", {
      id: id,
      ref: ref,
      css: [_GridCSS, container && (rowSpacing || columnSpacing) && _GridSpacingCSS, _HandleGridCSS],
      style: style,
      className: classNames(`DGN-UI-Grid${item ? '-item' : ''}`, className)
    }, jsx(GridContext.Provider, {
      value: columns
    }, children));
  }, [columns, columnSpacing, leftSpacing, rowSpacing, topSpacing, children, className, container, direction, id, item, lg, md, sm, spacing, style, verticalAlign, wrap, xl, xs, zeroMinWidth]);
}));
const getOffset = val => {
  const parse = parseFloat(val);
  return `${parse}${String(val).replace(String(parse), '') || 'px'}`;
};
const processSizeArray = sizeArr => {
  let lastValue = false;
  sizeArr = sizeArr.map(item => {
    if (item === false) return lastValue;else lastValue = item;
    return item;
  });
  return sizeArr;
};
const handleGrid = (size, value, columns) => {
  let css = '';
  if (value === true) {
    css = `
            flex-basis: 0;
            flex-grow: 1;
            max-width: 100%;
        `;
  } else if (value === 'auto') {
    css = `
            flex-basis: auto;
            flex-grow: 0;
            flex-shrink: 0;
            max-width: none;
            width: auto;
        `;
  } else if (typeof value === 'number' && value !== 0) {
    css = handleBreakpoints(columns, propValue => {
      return `
            flex-basis: ${Math.round(value / propValue * 10e7) / 10e5}%;
            flex-grow: 0;
            max-width: ${Math.round(value / propValue * 10e7) / 10e5}%;
        `;
    });
  }
  return `${themeBreakpoints.up(size)} {
        ${css}
    }`;
};
const GridCSS = (direction, zeroMinWidth, container, item, wrap, verticalAlign) => css`
    ${boxBorder};
    ${container && `
        display: flex;
        flex-wrap: ${wrap};
        width: 100%;
    `};
    ${item && `
        margin: 0;
        align-self: ${verticalAlign}
    `};
    ${zeroMinWidth && `min-width: 0;`}
    ${handleBreakpoints(direction, propValue => {
  return `flex-direction: ${propValue}`;
})};
`;
const GridSpacingCSS = (rowSpacing, columnSpacing, topSpacing, leftSpacing) => css`
    ${topSpacing && handleBreakpoints(topSpacing, propValue => {
  const spacing = typeof propValue === 'number' ? themeSpacing(propValue) : propValue;
  if (spacing !== '0px') {
    return `
            margin-top: -${getOffset(spacing)};
            `;
  }
  return '';
})};
    ${rowSpacing && handleBreakpoints(rowSpacing, propValue => {
  const spacing = typeof propValue === 'number' ? themeSpacing(propValue) : propValue;
  if (spacing !== '0px') {
    return `
            & > .DGN-UI-Grid-item {
                padding-top: ${getOffset(spacing)};
            }
            `;
  }
  return '';
})};
    ${leftSpacing && handleBreakpoints(leftSpacing, propValue => {
  const spacing = typeof propValue === 'number' ? themeSpacing(propValue) : propValue;
  if (spacing !== '0px') {
    return `
                margin-left: -${getOffset(spacing)};
                `;
  }
  return '';
})};
    ${columnSpacing && handleBreakpoints(columnSpacing, propValue => {
  const spacing = typeof propValue === 'number' ? themeSpacing(propValue) : propValue;
  if (spacing !== '0px') {
    return `
                width: calc(100% + ${getOffset(spacing)});
                & > .DGN-UI-Grid-item {
                padding-left: ${getOffset(spacing)};
            }
            `;
  }
  return '';
})};
`;
const HandleGridCSS = (sizeArr, columns) => css`
    ${processSizeArray(sizeArr).map((item, idx) => {
  return handleGrid(breakpointKeys[idx], item, columns);
})}
`;
Grid.defaultProps = {
  className: '',
  columns: 12,
  container: false,
  direction: 'row',
  item: false,
  lg: false,
  md: false,
  sm: false,
  spacing: 0,
  style: {},
  verticalAlign: 'auto',
  wrap: 'wrap',
  xl: false,
  xs: false,
  zeroMinWidth: false
};
Grid.propTypes = {
  /** The content of the component. */
  children: PropTypes.node,
  /** Style inline of component. */
  style: PropTypes.object,
  /** Class for component. */
  className: PropTypes.string,
  /** The number of columns. */
  columns: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.number, PropTypes.object]),
  /** Defines the horizontal space between the type `item` components. It overrides the value of the `spacing` prop. */
  columnSpacing: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])), PropTypes.number, PropTypes.object, PropTypes.string]),
  /** If true, the component will have the flex *container* behavior. You should be wrapping *items* with a *container*. */
  container: PropTypes.bool,
  /** Defines the `flex-direction` style property. It is applied for all screen sizes. */
  direction: PropTypes.oneOfType([PropTypes.oneOf(['column-reverse', 'column', 'row-reverse', 'row']), PropTypes.arrayOf(PropTypes.oneOf(['column-reverse', 'column', 'row-reverse', 'row'])), PropTypes.object]),
  /** If `true`, the component will have the flex *item* behavior. You should be wrapping *items* with a *container*. */
  item: PropTypes.bool,
  /** Defines the horizontal space of `container` components. */
  leftSpacing: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])), PropTypes.number, PropTypes.object, PropTypes.string]),
  /** If a number, it sets the number of columns the grid item uses. It can't be greater than the total number of columns of the container (12 by default). If 'auto', the grid item's width matches its content. If false, the prop is ignored. If true, the grid item's width grows to use the space available in the grid container. The value is applied for the `lg` breakpoint and wider screens if not overridden. */
  lg: PropTypes.oneOfType([PropTypes.oneOf(['auto']), PropTypes.number, PropTypes.bool]),
  /** If a number, it sets the number of columns the grid item uses. It can't be greater than the total number of columns of the container (12 by default). If 'auto', the grid item's width matches its content. If false, the prop is ignored. If true, the grid item's width grows to use the space available in the grid container. The value is applied for the `md` breakpoint and wider screens if not overridden. */
  md: PropTypes.oneOfType([PropTypes.oneOf(['auto']), PropTypes.number, PropTypes.bool]),
  /** Defines the vertical space between the type `item` components. It overrides the value of the `spacing` prop. */
  rowSpacing: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])), PropTypes.number, PropTypes.object, PropTypes.string]),
  /** If a number, it sets the number of columns the grid item uses. It can't be greater than the total number of columns of the container (12 by default). If 'auto', the grid item's width matches its content. If false, the prop is ignored. If true, the grid item's width grows to use the space available in the grid container. The value is applied for the `sm` breakpoint and wider screens if not overridden. */
  sm: PropTypes.oneOfType([PropTypes.oneOf(['auto']), PropTypes.number, PropTypes.bool]),
  /** Defines the space between the type `item` components. It can only be used on a type `container` component. */
  spacing: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])), PropTypes.number, PropTypes.object, PropTypes.string]),
  /** Defines the vertical space of `container` components. */
  topSpacing: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])), PropTypes.number, PropTypes.object, PropTypes.string]),
  /** Self-align of the grid item. */
  verticalAlign: PropTypes.oneOf(['auto', 'start', 'center', 'end']),
  /** Defines the flex-wrap style property. It's applied for all screen sizes. */
  wrap: PropTypes.oneOf(['nowrap', 'wrap-reverse', 'wrap']),
  /** If a number, it sets the number of columns the grid item uses. It can't be greater than the total number of columns of the container (12 by default). If 'auto', the grid item's width matches its content. If false, the prop is ignored. If true, the grid item's width grows to use the space available in the grid container. The value is applied for the `xl` breakpoint and wider screens if not overridden. */
  xl: PropTypes.oneOfType([PropTypes.oneOf(['auto']), PropTypes.number, PropTypes.bool]),
  /** If a number, it sets the number of columns the grid item uses. It can't be greater than the total number of columns of the container (12 by default). If 'auto', the grid item's width matches its content. If false, the prop is ignored. If true, the grid item's width grows to use the space available in the grid container. The value is applied for all the screen sizes with the lowest priority. */
  xs: PropTypes.oneOfType([PropTypes.oneOf(['auto']), PropTypes.number, PropTypes.bool]),
  /** If `true`, it sets `min-width: 0` on the item. Refer to the limitations section of the documentation to better understand the use case. */
  zeroMinWidth: PropTypes.bool
};
export { Grid };
export default OptionWrapper(Grid);